import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  FaTrophy,
  FaArrowRight,
  FaRocket,
} from 'react-icons/fa';

const SlideThree = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const steps = [
    {
      emoji: '📤',
      title: t('landing.slideThree.steps.createCourse.title', '1. Upload or describe'),
      description: t('landing.slideThree.steps.createCourse.description', 'Share a topic, documents, or a knowledge project'),
      step: 1,
      color: 'from-yellow-400 to-yellow-500',
      bgColor: 'from-yellow-50 to-yellow-100',
    },
    {
      emoji: '🧠',
      title: t('landing.slideThree.steps.addMaterials.title', '2. AI builds structure'),
      description: t('landing.slideThree.steps.addMaterials.description', 'Modules, interactive lessons, quizzes, and checkpoints'),
      step: 2,
      color: 'from-blue-400 to-blue-500',
      bgColor: 'from-blue-50 to-blue-100',
    },
    {
      emoji: '🎓',
      title: t('landing.slideThree.steps.inviteStudents.title', '3. Learn interactively'),
      description: t('landing.slideThree.steps.inviteStudents.description', 'Study with GenUI lessons, labs, practice from those lessons, and an AI tutor'),
      step: 3,
      color: 'from-purple-400 to-purple-500',
      bgColor: 'from-purple-50 to-purple-100',
    },
    {
      emoji: '📊',
      title: t('landing.slideThree.steps.monitorProgress.title', '4. Track & improve'),
      description: t('landing.slideThree.steps.monitorProgress.description', 'Analytics, spaced review, and certificates as you progress'),
      step: 4,
      color: 'from-pink-400 to-pink-500',
      bgColor: 'from-pink-50 to-pink-100',
    },
  ];

  return (
    <main className="w-full bg-white dark:bg-slate-950">
      <div className="relative overflow-hidden py-12 md:py-16 px-4 sm:px-6 lg:px-8">
        {/* Playful background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-yellow-50/30 to-indigo-50 dark:from-gray-950 dark:via-blue-950/10 dark:to-gray-950" />
        <div className="absolute top-20 right-20 w-64 h-64 bg-yellow-200/30 dark:bg-yellow-900/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-blue-200/20 dark:bg-blue-900/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto">
          {/* Header - Enhanced */}
          <div className="text-center max-w-3xl mx-auto mb-12">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-100 to-yellow-100 dark:from-blue-900/30 dark:to-yellow-900/20 rounded-full border-2 border-blue-300 dark:border-blue-700 mb-5 shadow-sm"
            >
              <FaRocket className="text-blue-600 dark:text-blue-400 text-sm animate-pulse" />
              <span className="text-xs font-bold text-blue-700 dark:text-blue-300 uppercase tracking-wide">
                {t('landing.slideThree.header.badge', 'Simple & Fun')}
              </span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white mb-4 leading-[1.1] tracking-tight"
            >
              {t('landing.slideThree.header.title', 'Get started in 4 simple steps')}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-base sm:text-lg md:text-base sm:text-lg text-gray-600 dark:text-gray-400 leading-relaxed font-medium"
            >
              {t('landing.slideThree.header.subtitle', 'Creating your course is as easy as 1-2-3-4')}
            </motion.p>
          </div>

          {/* Steps - Enhanced Duolingo-style */}
          <div className="mb-12">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {steps.map((s, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 40, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ delay: 0.3 + i * 0.1, duration: 0.6, type: 'spring' }}
                  whileHover={{ y: -8, scale: 1.02 }}
                  className="group relative p-6 bg-white dark:bg-slate-900 rounded-2xl border-2 border-slate-200 dark:border-slate-700 hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-2xl transition-all duration-300 overflow-hidden"
                >
                  {/* Colorful background accent */}
                  <div className={`absolute top-0 start-0 end-0 h-2 bg-gradient-to-r ${s.color}`} />
                  
                  {/* Step number badge - Enhanced */}
                  <div className={`absolute -top-3 -right-3 w-16 h-16 rounded-full bg-gradient-to-br ${s.color} flex items-center justify-center text-white font-extrabold text-xl shadow-xl border-4 border-white dark:border-gray-800 z-10`}>
                    {s.step}
                  </div>
                  
                  {/* Emoji icon */}
                  <div className="mt-2 mb-4">
                    <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${s.color} flex items-center justify-center text-5xl shadow-lg`}>
                      {s.emoji}
                    </div>
                  </div>
                  
                  {/* Content */}
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{s.title}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{s.description}</p>
                  
                  {/* Decorative element */}
                  <div className={`absolute bottom-0 end-0 w-24 h-24 bg-gradient-to-br ${s.bgColor} dark:opacity-10 rounded-full blur-2xl -z-0`} />
                </motion.div>
              ))}
            </div>
          </div>

          {/* Real product preview */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="max-w-5xl mx-auto mb-12"
          >
            <div className="overflow-hidden rounded-3xl shadow-2xl border-2 border-blue-200 dark:border-blue-800 bg-white dark:bg-slate-900">
              <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/20">
                <p className="text-xs font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                  {t('landing.slideThree.preview.label', 'Your learning path')}
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                  {t(
                    'landing.slideThree.preview.note',
                    'Real course structure with modules, lessons, and practice. This is the product, not a mockup.',
                  )}
                </p>
              </div>
              <img
                src="/Images/marketing/product-course-overview.png"
                alt={t(
                  'landing.slideThree.preview.imageAlt',
                  'Coursolo course overview showing modules, outcomes, and syllabus',
                )}
                className="w-full h-auto object-cover object-top"
                loading="lazy"
              />
            </div>
          </motion.div>

          {/* CTA - Enhanced */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.7 }}
            className="text-center"
          >
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white mb-3 tracking-tight">
              {t('landing.slideThree.cta.title', 'Ready to start learning?')}
            </h3>
            <p className="text-lg md:text-base sm:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto mb-8">
              {t('landing.slideThree.cta.subtitle', 'Join early learners and start your journey today')}
            </p>
            <motion.button
              whileHover={{ scale: 1.05, y: -3 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate('/signup')}
              className="inline-flex items-center gap-3 px-10 py-4 rounded-2xl font-bold text-lg text-white bg-blue-600 hover:bg-blue-700 shadow-xl shadow-blue-500/30 hover:shadow-2xl hover:shadow-blue-500/40 transition-all duration-200"
            >
              <FaTrophy className="text-base" />
              {t('landing.slideThree.cta.button', 'Start Learning Free')}
              <FaArrowRight className="text-base" />
            </motion.button>
          </motion.div>
        </div>
      </div>
    </main>
  );
};

export default SlideThree;
